/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { z } from 'zod';

import { ApiError, ContractError, type FieldProblem } from '@/lib/queryClient';

/**
 * The one place a network request is made.
 *
 * Two rules are enforced here rather than left to each call site:
 *
 * - `credentials: 'include'` sends the httpOnly session cookie. There is no
 *   token in JavaScript to attach, and there never will be (AUTH-5).
 * - Every response is parsed with a Zod schema before it reaches a component.
 *   The generated type describes the contract; Zod proves the response
 *   honoured it (FE-3).
 * - Every request names itself as Vellum's own (`CSRF_HEADER`), which the
 *   server requires before it will change anything.
 */

/**
 * The header that proves a request came from Vellum's own page.
 *
 * The session is a cookie, and a browser attaches a cookie to a request from
 * any site that points at us. What another site cannot do is add a header of
 * its choosing to a cross-origin request without first asking permission, and
 * the server grants that permission to nobody. So the server refuses a write
 * that arrives without this header, and a forged form post on some other page
 * is turned away before it touches a thing (`SEC-04`).
 *
 * Sent on every request rather than only on writes: a read does not need it,
 * but one rule is easier to keep than two.
 */
export const CSRF_HEADER = 'X-Requested-With';
export const CSRF_VALUE = 'vellum';

/**
 * The header that marks a read as the page asking on its own.
 *
 * The server's idle clock moves on every request it serves, so a screen that
 * re-asks every thirty seconds would keep a session alive for somebody who
 * walked away hours ago. A request carrying this header is answered without
 * extending the session. Whether somebody is really there is the session
 * guard's to report, from keystrokes and clicks (`SESS-5`).
 */
export const POLL_HEADER = 'X-Vellum-Poll';
export const POLL_VALUE = '1';

/** One field the server refused, and why -- never what was typed in it. */
export const fieldProblemSchema = z.object({
  field: z.string(),
  reason: z.string(),
  /** The reason in words, where the server has them: put on the field itself. */
  message: z.string().nullable().default(null),
});

/** The server's RFC 9457 problem body. */
export const problemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number(),
  detail: z.string(),
  correlation_id: z.string(),
  /** A machine-readable reason, on the problems that carry one. */
  code: z.string().optional(),
  /** When asking again is allowed, on a refusal that says (`REM-5`). */
  retry_after: z.string().optional(),
  /** On a 422: each field refused, and why. */
  errors: z.array(fieldProblemSchema).nullable().optional(),
  /** On an unfinished agreement: the questions the refusal is about, by key. */
  missing: z.array(z.string()).optional(),
});

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /**
   * JSON by default. `FormData` is sent as-is: the browser writes the
   * multipart boundary into the Content-Type itself, so setting that header
   * here would produce a body the server cannot parse.
   */
  body?: unknown;
  signal?: AbortSignal;
  /**
   * A read the page makes on its own, on a timer -- not something a person
   * did. The server answers it without extending the session (`POLL_HEADER`).
   */
  poll?: boolean;
}

async function toApiError(response: Response): Promise<ApiError> {
  let title = response.statusText || 'Request failed';
  let problemType = 'about:blank';
  let correlationId = response.headers.get('X-Correlation-ID') ?? '';
  let detail = '';
  let code: string | undefined;
  let retryAfter: string | undefined;
  let fields: FieldProblem[] = [];
  let missing: string[] = [];

  try {
    const parsed = problemSchema.safeParse(await response.json());
    if (parsed.success) {
      title = parsed.data.title;
      problemType = parsed.data.type;
      correlationId = parsed.data.correlation_id || correlationId;
      detail = parsed.data.detail;
      code = parsed.data.code;
      retryAfter = parsed.data.retry_after;
      fields = (parsed.data.errors ?? []).map((problem) => ({
        field: problem.field,
        reason: problem.reason,
        message: problem.message ?? '',
      }));
      missing = parsed.data.missing ?? [];
    }
  } catch {
    // A body that is not problem+json tells us nothing extra. The status and
    // the correlation id are enough to act on.
  }

  return new ApiError({
    status: response.status,
    title,
    problemType,
    correlationId,
    detail,
    code,
    retryAfter,
    fields,
    missing,
  });
}

/**
 * Perform a request and validate the response against `schema`.
 *
 * @throws ApiError when the server returns a non-2xx status.
 * @throws ContractError when the response does not match the contract.
 */
export async function request<T>(
  path: string,
  schema: z.ZodType<T>,
  options: RequestOptions = {},
): Promise<T> {
  const { method = 'GET', body, signal, poll = false } = options;

  // Built up rather than passed with `undefined` values: under
  // exactOptionalPropertyTypes, an explicit `undefined` is not the same as an
  // omitted key, and RequestInit does not accept one.
  const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_VALUE };
  if (poll) headers[POLL_HEADER] = POLL_VALUE;

  const init: RequestInit = {
    method,
    // The session is an httpOnly cookie. Nothing is read from storage (AUTH-5).
    credentials: 'include',
    headers,
  };

  // FormData is left without a Content-Type on purpose: the browser writes one
  // carrying the multipart boundary, and a header set here would replace it.
  if (body instanceof FormData) {
    init.body = body;
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(body);
  }

  if (signal) {
    init.signal = signal;
  }

  const response = await fetch(path, init);

  if (!response.ok) {
    throw await toApiError(response);
  }

  const parsed = schema.safeParse(
    response.status === 204 ? undefined : await response.json(),
  );

  // Thrown as our own error rather than Zod's: what a person needs to see is
  // that the two halves disagree, not which field was missing.
  if (!parsed.success) throw new ContractError(path, parsed.error);

  return parsed.data;
}

/**
 * Fetch an image, or null when there is none (a 204 or a 404).
 *
 * For the few images Vellum serves itself -- the company logo -- which a page
 * shows from the bytes rather than from an address, so that "there is none"
 * can be told apart from "it failed" and fall back to something built in.
 *
 * @throws ApiError when the server answers with any other failure.
 */
export async function requestImage(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<Blob | null> {
  const init: RequestInit = {
    credentials: 'include',
    headers: { [CSRF_HEADER]: CSRF_VALUE },
  };
  if (options.signal) init.signal = options.signal;

  const response = await fetch(path, init);
  // No such image: `204` where the server was asked for one only if there is
  // one (the app's uploaded logo), `404` where there was none to give.
  if (response.status === 204 || response.status === 404) return null;
  if (!response.ok) throw await toApiError(response);
  return response.blob();
}

/**
 * Fetch a file's bytes, for a page that draws them itself.
 *
 * For the counterparty's own document, which the spot editor draws page by
 * page in the browser: the bytes come through Vellum's own permission check,
 * same-origin and with the session cookie, and never from a store's link
 * (`DOC-6`). Unlike `requestImage`, a missing file is a failure here -- the
 * page asked for a document it was told exists.
 *
 * @throws ApiError when the server answers with any failure.
 */
export async function requestFile(
  path: string,
  options: { signal?: AbortSignal } = {},
): Promise<ArrayBuffer> {
  const init: RequestInit = {
    credentials: 'include',
    headers: { [CSRF_HEADER]: CSRF_VALUE },
  };
  if (options.signal) init.signal = options.signal;

  const response = await fetch(path, init);
  if (!response.ok) throw await toApiError(response);
  return response.arrayBuffer();
}
